import React from 'react';
import { RestartAlt } from '@mui/icons-material';
import { Button, Chip, Stack, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { InputField, AutocompleteValueField } from 'components/form';
import { convertEnumToSelectOptions } from 'utils/selectOptionsHelper';
import { DealActivityStatus, DealActivityEventType } from '@trnsact/trnsact-shared-types';

import { ListingFiltersValues } from './types';
import { INITIAL_FILTERS, quickFiltersConfig } from '../../constants';

type Filters = ListingFiltersValues['filters'];

const isQuickFilterActive = (value: Partial<Filters>, filters: Filters): boolean =>
  Object.entries(value).every(([key, val]) => JSON.stringify(val) === JSON.stringify(filters[key as keyof Filters]));

export const ListingFilters = () => {
  const { reset, control, setValue, getValues } = useFormContext<ListingFiltersValues>();

  const filters = useWatch({ control, name: 'filters' });

  const handleChangedQuickFilter = (value: Partial<Filters>) => {
    setValue('filters', {
      ...getValues('filters'),
      ...value,
    });
  };

  const hasActiveFilters = JSON.stringify(filters) !== JSON.stringify(INITIAL_FILTERS.filters);

  return (
    <Stack gap="0.5rem">
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" gap="0.5rem" alignItems="center">
          {quickFiltersConfig.map(item => {
            const active = isQuickFilterActive(item.value, filters);

            return (
              <Chip
                size="small"
                key={item.label}
                color={active ? 'primary' : 'default'}
                variant={active ? 'filled' : 'outlined'}
                label={<Typography>{item.label}</Typography>}
                onClick={() => handleChangedQuickFilter(item.value)}
              />
            );
          })}
        </Stack>

        <Button size="small" onClick={() => reset()} startIcon={<RestartAlt />} disabled={!hasActiveFilters}>
          Reset
        </Button>
      </Stack>

      <Stack direction="row" gap="0.5rem">
        <InputField
          label="Search"
          control={control}
          name="filters.search"
          inputProps={{
            size: 'small',
            variant: 'outlined',
          }}
        />

        <AutocompleteValueField
          multiple
          size="small"
          control={control}
          name="filters.type"
          options={convertEnumToSelectOptions(DealActivityEventType)}
          textFieldProps={{
            size: 'small',
            label: 'Type',
            variant: 'outlined',
          }}
        />

        <AutocompleteValueField
          multiple
          size="small"
          control={control}
          name="filters.status"
          options={convertEnumToSelectOptions(DealActivityStatus)}
          textFieldProps={{
            size: 'small',
            label: 'Status',
            variant: 'outlined',
          }}
        />
      </Stack>
    </Stack>
  );
};
